/**
 * Deploy 5 · C1 — Recargo manual por horario fuera de servicio (pantalla Cobrar).
 * Entra como ítem `premium_surcharge` del subtotal (se reparte como el servicio).
 */
import { useState } from 'react';
import { MoonStar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { tr } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';

const PCT_PRESETS = [10, 25, 50, 100];
const money = (v) => fmtMoney(v);

export const OutsideHoursSurcharge = ({ basePrice, value, onChange }) => {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('pct'); // pct | fixed
  const [pct, setPct] = useState(25);
  const [fixed, setFixed] = useState('');

  const compute = (m, p, f) => {
    if (m === 'pct') {
      const n = Math.min(Math.max(Number(p || 0), 0), 100);
      return Math.round(basePrice * n) / 100;
    }
    return Math.round(Math.max(Number(f || 0), 0) * 100) / 100;
  };
  const apply = (on, m, p, f) => onChange(on ? { amount: compute(m, p, f), mode: m, value: m === 'pct' ? Number(p) : Number(f) } : null);

  return (
    <div className="bg-amber-500/5 border border-amber-500/40 rounded-xl p-3 space-y-2" data-testid="sale-outside-hours-block">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <MoonStar className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground">{tr("Cita fuera de horario — ¿Aplicar cargo extra por horario fuera de servicio?")}</p>
            <p className="text-[10px] text-muted-foreground">{tr("Se suma al precio del servicio antes de cobrar y se reparte igual que el servicio.")}</p>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={(on) => { setEnabled(on); apply(on, mode, pct, fixed); }} data-testid="sale-outside-hours-toggle" />
      </div>
      {enabled && (
        <div className="space-y-2" data-testid="sale-outside-hours-controls">
          <div className="flex items-center gap-1.5 flex-wrap">
            {PCT_PRESETS.map((p) => (
              <button
                key={p} type="button"
                onClick={() => { setMode('pct'); setPct(p); apply(true, 'pct', p, fixed); }}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${mode === 'pct' && Number(pct) === p ? 'bg-amber-500/20 border-amber-500/70 text-amber-300' : 'bg-card border-border text-muted-foreground'}`}
                data-testid={`sale-surcharge-pct-${p}`}
              >
                +{p}%
              </button>
            ))}
            <button
              type="button" onClick={() => { setMode('fixed'); apply(true, 'fixed', pct, fixed); }}
              className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${mode === 'fixed' ? 'bg-amber-500/20 border-amber-500/70 text-amber-300' : 'bg-card border-border text-muted-foreground'}`}
              data-testid="sale-surcharge-fixed-mode"
            >{tr("Monto fijo")}</button>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'pct' ? (
              <Input value={pct} onChange={(e) => { setPct(e.target.value); apply(true, 'pct', e.target.value, fixed); }} inputMode="numeric" min={10} max={100}
                className="h-9 rounded-lg bg-card border-border text-sm w-24" data-testid="sale-surcharge-pct-input" />
            ) : (
              <Input value={fixed} onChange={(e) => { setFixed(e.target.value); apply(true, 'fixed', pct, e.target.value); }} placeholder="$" inputMode="decimal"
                className="h-9 rounded-lg bg-card border-border text-sm w-24" data-testid="sale-surcharge-fixed-input" />
            )}
            <p className="text-xs text-foreground/80 flex-1">
              {mode === 'pct' ? tr("{0}% de {1}", pct || 0, money(basePrice)) : tr("Monto fijo")} → <b className="text-amber-300 tabular-nums" data-testid="sale-surcharge-amount">{money(value?.amount || 0)}</b>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
